import React, { useContext, useState } from "react"
import { Link, useLocation } from "react-router-dom"
import { SmartDateFormat } from "@util/dates"
import { UserContext } from "../../HomeApp"
import { react_post_path } from "../../../routes"
import clsx from "clsx"
import { ShareUrlButton } from "@components/ShareUrlButton"
import { AddToCalendarModal } from "@components/shared/AddToCalendarModal"
import { formatPrice } from "@util/ticketUtils"
import { TICKET_TYPES, PRICING_TYPES } from "../../../constants/events"
import { CalendarPlus, Pencil } from "react-bootstrap-icons"

interface PostCollaborator {
  band_id: number
  status: string
}

interface BnVenue {
  name: string
}

interface CalendarEventPost {
  id: number
  band_id: number
  title: string
  status: string
  nada_path: string
  nada_url: string
  start_dt: string
  canceled_at?: string | null
  ticket_type?: string | null
  pricing_type?: string | null
  ticket_price?: number | null
  ticket_price_max?: number | null
  user_has_purchased_ticket?: boolean
  user_ticket_count?: number
  has_new_comments_since_show?: boolean
  add_to_calendar_url?: string
  outlook_calendar_url?: string
  ics_calendar_url?: string
  post_collaborators?: PostCollaborator[]
  bn_venue?: BnVenue | null
  venue_name?: string | null
}

interface CalendarEventProps {
  calendarEvent: CalendarEventPost
  isPast?: boolean
}

const CalendarEvent: React.FC<CalendarEventProps> = ({
  calendarEvent,
  isPast = false,
}) => {
  const { user } = useContext(UserContext)
  const location = useLocation()
  const [showAddToCalendar, setShowAddToCalendar] = useState(false)
  const isOwner = calendarEvent.band_id == user.band_id
  const pendingCollaboration = calendarEvent.post_collaborators?.find(
    (c) => c.band_id === user.band_id && c.status === "pending",
  )

  const ticketType = calendarEvent.ticket_type
  const pricingType = calendarEvent.pricing_type
  const isFree =
    ticketType === TICKET_TYPES.BN_TICKETS.value
      ? pricingType === PRICING_TYPES.FREE.value
      : !pricingType || pricingType === PRICING_TYPES.FREE.value

  const isAtDoor = ticketType === TICKET_TYPES.NO_ADVANCE_TICKETS.value
  const atDoor = isAtDoor ? " at door" : ""

  let priceTag: string | null = null
  if (!isFree) {
    if (pricingType === PRICING_TYPES.SLIDING_SCALE.value) {
      const min = formatPrice(calendarEvent.ticket_price)
      const max = formatPrice(calendarEvent.ticket_price_max)
      if (min && max) priceTag = `${min}–${max}${atDoor}`
    } else {
      const price = formatPrice(calendarEvent.ticket_price)
      if (price) priceTag = `${price}${atDoor}`
    }
  }

  const ticketCount = calendarEvent.user_ticket_count || 0

  return (
    <li className="text-sm mb-4">
      <div className="mt-0.5 flex flex-wrap items-center gap-1">
        {isOwner && (
          <Link
            to={{
              pathname: calendarEvent.nada_path,
              state: { from: location },
            }}
            className="inline-block text-xs font-medium bg-green-100 text-green-800 px-1 rounded"
          >
            Your Event
          </Link>
        )}
        {calendarEvent.status === "unpublished" && (
          <span className="inline-block text-xs font-medium bg-gray-100 text-gray-500 px-1 rounded">
            Unlisted
          </span>
        )}
        {pendingCollaboration && (
          <Link
            to={{
              pathname: calendarEvent.nada_path,
              state: { from: location },
            }}
            className="inline-block text-xs font-medium bg-yellow-100 text-yellow-800 px-1 rounded"
          >
            collaboration invitation
          </Link>
        )}
        {calendarEvent.user_has_purchased_ticket && (
          <span className="inline-block text-xs font-medium bg-purple-100 text-purple-800 px-1 rounded">
            {ticketCount > 1
              ? `${ticketCount} Tickets Purchased`
              : "Ticket Purchased"}
          </span>
        )}
        {!isPast && (isFree ? (
          <span className="inline-block text-xs font-medium bg-blue-100 text-blue-800 px-1 rounded">
            Free
          </span>
        ) : priceTag ? (
          <span className="inline-block text-xs font-medium bg-gray-100 text-gray-700 px-1 rounded">
            {priceTag}
          </span>
        ) : null)}
        {isPast && calendarEvent.has_new_comments_since_show && (
          <Link
            to={{ pathname: calendarEvent.nada_path, state: { from: location } }}
            className="inline-block text-xs font-medium bg-blue-100 text-blue-800 px-1 rounded"
          >
            New comments
          </Link>
        )}
        <span className="inline-flex items-center gap-3 ml-auto">
          {isOwner && (
            <Link
              to={react_post_path(calendarEvent.id, { edit: true })}
              className="text-blue-600"
              aria-label="Edit"
              title="Edit"
            >
              <Pencil size={14} />
            </Link>
          )}
          <button
            type="button"
            className="text-blue-600 focus:outline-none"
            onClick={() => setShowAddToCalendar(true)}
            aria-label="Add to calendar"
            title="Add to calendar"
          >
            <CalendarPlus size={14} />
          </button>
          <ShareUrlButton
            nadaUrl={calendarEvent.nada_url}
            renderMode="icon"
            iconSize={17}
            alignment="align-text-top"
          />
        </span>
      </div>
      <div className="leading-none">
        <Link
          to={{ pathname: calendarEvent.nada_path, state: { from: location } }}
          className={clsx(
            "uppercase",
            isPast ? "text-red-600" : "font-bold",
            isPast && calendarEvent.has_new_comments_since_show && "font-bold",
          )}
        >
          {SmartDateFormat({
            dateStr: calendarEvent.start_dt,
            includeTime: true,
          })}
        </Link>
        {isOwner && calendarEvent.status === "needs_approved" ? (
          <>
            {" - "}
            <span className="font-bold text-red-600 uppercase">
              Just Announced!
            </span>
          </>
        ) : null}
      </div>
      <Link
        className={clsx(
          "display-block block font-bold italic",
          calendarEvent.status === "canceled" || calendarEvent.canceled_at
            ? "text-bn-neon-red"
            : "text-gray-700",
        )}
        to={{ pathname: calendarEvent.nada_path, state: { from: location } }}
      >
        {calendarEvent.status === "canceled" || calendarEvent.canceled_at ? (
          <>
            Canceled!{" "}
            <span className="line-through">{calendarEvent.title}</span>
          </>
        ) : (
          calendarEvent.title
        )}
      </Link>
      <Link
        to={{ pathname: calendarEvent.nada_path, state: { from: location } }}
        className={clsx(
          "leading-tight text-sm",
          calendarEvent.bn_venue && "text-green-700 font-semibold",
        )}
      >
        {calendarEvent.bn_venue
          ? calendarEvent.bn_venue.name
          : calendarEvent.venue_name}
      </Link>

      {isOwner && calendarEvent.status === "needs_approved" ? (
        <div className="text-red-600 text-xs leading none">
          This event is NOT public yet. Click edit to add details and publish
          it.
        </div>
      ) : null}

      {showAddToCalendar && (
        <AddToCalendarModal
          eventTitle={calendarEvent.title}
          googleUrl={calendarEvent.add_to_calendar_url}
          outlookUrl={calendarEvent.outlook_calendar_url}
          icsUrl={calendarEvent.ics_calendar_url}
          onClose={() => setShowAddToCalendar(false)}
        />
      )}
    </li>
  )
}

export default CalendarEvent
